<script lang="ts">
    import { onMount } from "svelte";
    import { me, GetToken } from "../user";
    import { service } from "@frontend/services";
    import { push } from "svelte-spa-router";
    import { Configuration } from "@frontend/walletapi";
    import { translate, currentLanguage } from "../Translator";


    let configurations: Configuration[] = [];
    let loading = false;
    let searchTerm = "";
    let showAddModal = false;
    let showEditModal = false;
    let editingConfig: Configuration | null = null;
    let newConfig = {
        Key: "",
        Value: ""
    };

    // Opciones predefinidas para el dropdown de claves de configuración
    const configurationKeys = [
        { value: "session_time_limit_minutes", label: "Límite de Tiempo de Sesión (minutos)", type: "number" },
        { value: "session_reminder_enabled", label: "Recordatorio de Sesión Habilitado", type: "boolean" }
    ];

    // Función para obtener el tipo de input basado en la clave
    function getInputType(key: string): string {
        const configKey = configurationKeys.find(k => k.value === key);
        return configKey?.type || "text";
    }

    // Función para cargar la lista de configuraciones
    async function loadConfigurations() {
        const token = GetToken();
        if (!token) return;
        
        loading = true;
        try {
            const result = await service.walletapi.configurationList({ Token: token });
            if (result.Success && result.Data) {
                configurations = result.Data;
            } else {
                console.error("Error loading configurations:", result.ErrorMessage);
            }
        } catch (error) {
            console.error("Error loading configurations:", error);
        } finally {
            loading = false;
        }
    }

    // Función para agregar nueva configuración
    async function addConfiguration() {
        const token = GetToken();
        if (!token || !newConfig.Key || !newConfig.Value) return;
        
        try {
            const result = await service.walletapi.addConfiguration({
                Token: token,
                Data: {
                    Key: newConfig.Key,
                    Value: newConfig.Value
                }
            });
            
            if (result.Success) {
                // Recargar la lista
                await loadConfigurations();
                // Limpiar el formulario
                newConfig = { Key: "", Value: ""};
                showAddModal = false;
            } else {
                console.error("Error adding configuration:", result.ErrorMessage);
            }
        } catch (error) {
            console.error("Error adding configuration:", error);
        }
    }

    // Función para editar configuración
    function editConfiguration(config: Configuration) {
        editingConfig = { ...config };
        showEditModal = true;
    }

    // Función para guardar cambios
    async function saveConfiguration() {
        const token = GetToken();
        if (!token || !editingConfig) return;
        
        try {
            const result = await service.walletapi.editConfiguration({
                Token: token,
                Data: {
                    Id: editingConfig.Id,
                    Key: editingConfig.Key,
                    Value: editingConfig.Value
                }
            });
            
            if (result.Success) {
                await loadConfigurations();
                editingConfig = null;
                showEditModal = false;
            } else {
                console.error("Error updating configuration:", result.ErrorMessage);
            }
        } catch (error) {
            console.error("Error updating configuration:", error);
        }
    }

    // Función para cancelar edición
    function cancelEdit() {
        editingConfig = null;
        showEditModal = false;
    }

    // Función para cancelar agregar
    function cancelAdd() {
        newConfig = { Key: "", Value: "" };
        showAddModal = false;
    }

    // Filtrar configuraciones por término de búsqueda
    $: filteredConfigurations = configurations.filter(config => 
        config.Key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        config.Value?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    onMount(() => {
        // Verificar autenticación primero
        if (!$me) {
            push("/user/Login");
            return;
        }
        
        loadConfigurations();
    });
</script>

<div class="ui container" style="padding: 20px;">
    <h1 class="ui centered dividing header">
                {$translate('Configuration')}
    </h1>

    <!-- Barra de búsqueda y botón agregar -->
    <div class="ui segment">
        <div class="ui grid">
            <div class="twelve wide column">
                <div class="ui fluid search">
                    <div class="ui icon input">
                        <input 
                            class="prompt" 
                            type="text" 
                            placeholder={$translate('Search')} 
                            bind:value={searchTerm}
                        >
                        <i class="search icon"></i>
                    </div>
                </div>
            </div>
            <div class="four wide column">
                <button 
                    class="ui primary button fluid" 
                    on:click={() => showAddModal = true}
                >
                    <i class="plus icon"></i>
                    {$translate('Add')}
                </button>
            </div>
        </div>
    </div>

    <!-- Modal para agregar configuración -->
    {#if showAddModal}
        <div class="ui modal active" style="display: block !important;">
            <div class="header">
                <i class="plus circle icon"></i>
                {$translate('Add Configuration')}
            </div>
            <div class="content">
                <form class="ui form" on:submit|preventDefault={addConfiguration}>
                    <div class="field">
                        <label>{$translate('Key')}</label>
                        <select 
                            bind:value={newConfig.Key} 
                            class="ui dropdown"
                            required
                        >
                            <option value="">{$translate('Select configuration key')}</option>
                            {#each configurationKeys as keyOption}
                                <option value={keyOption.value}>{keyOption.label}</option>
                            {/each}
                        </select>
                    </div>
                    <div class="field">
                        <label>{$translate('Value')}</label>
                        {#if getInputType(newConfig.Key) === 'boolean'}
                            <select 
                                bind:value={newConfig.Value} 
                                class="ui dropdown"
                                required
                            >
                                <option value="">{$translate('Select value')}</option>
                                <option value="true">True</option>
                                <option value="false">False</option>
                            </select>
                        {:else if getInputType(newConfig.Key) === 'number'}
                            <input 
                                type="number" 
                                bind:value={newConfig.Value} 
                                placeholder={$translate('Configuration value')}
                                required
                            >
                        {:else}
                            <input 
                                type="text" 
                                bind:value={newConfig.Value} 
                                placeholder={$translate('Configuration value')}
                                required
                            >
                        {/if}
                    </div>
                </form>
            </div>
            <div class="actions">
                <button 
                    class="ui primary button" 
                    on:click={addConfiguration}
                >
                    <i class="save icon"></i>
                    {$translate('Save')}
                </button>
                <button 
                    class="ui secondary button" 
                    on:click={cancelAdd}
                >
                    <i class="cancel icon"></i>
                    {$translate('Cancel')}
                </button>
            </div>
        </div>
        <div class="ui dimmer active" style="display: block !important;"></div>
    {/if}

    <!-- Modal para editar configuración -->
    {#if showEditModal && editingConfig}
        <div class="ui modal active" style="display: block !important;">
            <div class="header">
                <i class="edit icon"></i>
                {$translate('Edit Configuration')}
            </div>
            <div class="content">
                <form class="ui form" on:submit|preventDefault={saveConfiguration}>
                    <div class="field">
                        <label>ID</label>
                        <input 
                            type="text" 
                            value={editingConfig.Id} 
                            readonly
                            class="ui input"
                        >
                    </div>
                    <div class="field">
                        <label>{$translate('Key')}</label>
                        <input 
                            type="text" 
                            value={editingConfig.Key} 
                            readonly
                            class="ui input"
                        >
                    </div>
                    <div class="field">
                        <label>{$translate('Value')}</label>
                        {#if getInputType(editingConfig.Key) === 'boolean'}
                            <select 
                                bind:value={editingConfig.Value} 
                                class="ui dropdown"
                                required
                            >
                                <option value="">{$translate('Select value')}</option>
                                <option value="true">True</option>
                                <option value="false">False</option>
                            </select>
                        {:else if getInputType(editingConfig.Key) === 'number'}
                            <input 
                                type="number" 
                                bind:value={editingConfig.Value} 
                                placeholder={$translate('Configuration value')}
                                required
                            >
                        {:else}
                            <input 
                                type="text" 
                                bind:value={editingConfig.Value} 
                                placeholder={$translate('Configuration value')}
                                required
                            >
                        {/if}
                    </div>
                </form>
            </div>
            <div class="actions">
                <button 
                    class="ui primary button" 
                    on:click={saveConfiguration}
                >
                    <i class="save icon"></i>
                    {$translate('Save')}
                </button>
                <button 
                    class="ui secondary button" 
                    on:click={cancelEdit}
                >
                    <i class="cancel icon"></i>
                    {$translate('Cancel')}
                </button>
            </div>
        </div>
        <div class="ui dimmer active" style="display: block !important;"></div>
    {/if}

    <!-- Lista de configuraciones -->
    <div class="ui segment">
        {#if loading}
            <div class="ui active centered inline loader"></div>
        {:else if filteredConfigurations.length === 0}
            <div class="ui placeholder segment">
                <div class="ui icon header">
                    <i class="cog icon"></i>
                    {$translate('No configurations found')}
                </div>
            </div>
        {:else}
            <div class="ui table">
                <table class="ui celled table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>{$translate('Key')}</th>
                            <th>{$translate('Value')}</th>
                            <th>{$translate('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each filteredConfigurations as config}
                            <tr>
                                <td>{config.Id}</td>
                                <td>
                                    {configurationKeys.find(k => k.value === config.Key)?.label || config.Key}
                                </td>
                                <td>{config.Value}</td>
                                <td>
                                    <button 
                                        class="ui blue icon button mini" 
                                        on:click={() => editConfiguration(config)}
                                    >
                                        <i class="edit icon"></i>
                                    </button>
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
    </div>

    <!-- Información del sistema -->
    <div class="ui segment">
        <h3 class="ui header">
            <i class="info circle icon"></i>
            {$translate('System Information')}
        </h3>
        <div class="ui list">
            <div class="item">
                <i class="code branch icon"></i>
                <div class="content">
                    <div class="header">{$translate('Total Configurations')}</div>
                    <div class="description">{configurations.length}</div>
                </div>
            </div>
            <div class="item">
                <i class="calendar icon"></i>
                <div class="content">
                    <div class="header">{$translate('Last Updated')}</div>
                    <div class="description">{new Date().toLocaleDateString()}</div>
                </div>
            </div>
        </div>
    </div>
</div>

<style>
    .ui.container {
        max-width: 1200px !important;
    }
    
    .ui.segment {
        margin-top: 20px;
    }
    
    .ui.form .field {
        margin-bottom: 20px;
    }
    
    .ui.button {
        margin-right: 10px;
    }
    
    .ui.table {
        margin-top: 20px;
    }
    
    .ui.search {
        margin-bottom: 0;
    }
    
    .ui.dropdown {
        width: 100%;
    }
    
    /* Estilos para el modal */
    .ui.modal {
        position: fixed !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) !important;
        z-index: 1001 !important;
        margin: 0 !important;
    }
    
    .ui.dimmer {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        z-index: 1000 !important;
        background-color: rgba(0, 0, 0, 0.5) !important;
    }
    
    .ui.modal .header {
        background-color: #f8f9fa;
        border-bottom: 1px solid #dee2e6;
        padding: 1rem 1.5rem;
    }
    
    .ui.modal .content {
        padding: 1.5rem;
    }
    
    .ui.modal .actions {
        padding: 1rem 1.5rem;
        border-top: 1px solid #dee2e6;
        background-color: #f8f9fa;
    }
    
    /* Estilos para campos de solo lectura */
    input[readonly] {
        background-color: #f8f9fa !important;
        color: #6c757d !important;
        cursor: not-allowed !important;
        border-color: #dee2e6 !important;
    }
    
    input[readonly]:focus {
        border-color: #dee2e6 !important;
        box-shadow: none !important;
    }
</style>